import React, { useMemo, useState } from 'react';
import './AnalysisTable.css';

export interface AnalysisTableColumn {
  key: string;
  label: string;
  align?: 'left' | 'right';
  /** Optional formatter for display; sorting uses the raw value. */
  format?: (value: any) => string;
}

interface AnalysisTableProps {
  columns: AnalysisTableColumn[];
  rows: Array<Record<string, any>>;
  /** Column key to sort by initially. */
  defaultSortKey?: string;
  emptyMessage?: string;
  maxRows?: number;
}

const AnalysisTable: React.FC<AnalysisTableProps> = ({
  columns,
  rows,
  defaultSortKey,
  emptyMessage = 'No data for this range.',
  maxRows,
}) => {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av ?? '').localeCompare(String(bv ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [rows, sortKey, sortDir]);

  const visible = maxRows ? sorted.slice(0, maxRows) : sorted;

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(dir => (dir === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (rows.length === 0) {
    return <p className="analysis-table-empty">{emptyMessage}</p>;
  }

  return (
    <div className="analysis-table-wrap">
      <table className="analysis-table">
        <thead>
          <tr>
            {columns.map(col => (
              <th
                key={col.key}
                className={col.align === 'right' ? 'analysis-table-right' : ''}
                onClick={() => handleSort(col.key)}
              >
                {col.label}
                {sortKey === col.key && (
                  <span className="analysis-table-sort">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visible.map((row, i) => (
            <tr key={i}>
              {columns.map(col => (
                <td key={col.key} className={col.align === 'right' ? 'analysis-table-right' : ''}>
                  {col.format ? col.format(row[col.key]) : String(row[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {maxRows && sorted.length > maxRows && (
        <p className="analysis-table-more">Showing {maxRows} of {sorted.length} rows</p>
      )}
    </div>
  );
};

export default AnalysisTable;
